/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import user from 'js/lib/user';

import { InlineNotification, Typography2 } from '@coursera/cds-core';
import { SvgApple } from '@coursera/coursera-ui/svg';

import LinkButton from 'bundles/third-party-auth/components/AccountLink/LinkButton';
import { useAccountLink } from 'bundles/third-party-auth/components/AccountLink/hooks/useAccountLink';

import _t from 'i18n!nls/third-party-auth';

const styles = {
  row: css`
    display: flex;
    flex-direction: column;
  `,
  topRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--cds-spacing-150);
  `,
  label: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    margin: 0;
  `,
  liveRegion: css`
    min-height: 0;

    &:not(:empty) {
      margin-top: var(--cds-spacing-200);
    }
  `,
};

const AccountLinkApple = () => {
  const {
    isCurrentAccount,
    isLinked,
    isDisabled,
    isLoading,
    successCode,
    errorMessage,
    successMessage,
    handleLink,
    handleDelink,
  } = useAccountLink('apple');

  // Apple's "Hide My Email" uses a privaterelay address; after unlinking, the user should update it
  const userData = user.get();
  const hasPrivateRelayEmail =
    typeof userData?.email_address === 'string' && userData.email_address.indexOf('privaterelay.appleid.com') !== -1;
  const showEmailUpdateWarning = isCurrentAccount && successCode === 'delink' && hasPrivateRelayEmail;

  return (
    <div css={styles.row} data-testid="account-link-apple">
      <div css={styles.topRow}>
        <Typography2 variant="subtitleMedium" component="h3" css={styles.label}>
          <SvgApple size={20} aria-hidden />
          {_t('Apple')}
        </Typography2>
        <LinkButton
          provider="apple"
          linked={isLinked}
          disabled={isDisabled}
          loading={isLoading}
          onLink={handleLink}
          onDelink={() => handleDelink()}
        />
      </div>
      <div role="status" aria-live="polite" aria-atomic="true" css={styles.liveRegion}>
        {successMessage && !showEmailUpdateWarning && (
          <InlineNotification severity="success">{successMessage}</InlineNotification>
        )}
        {showEmailUpdateWarning && (
          <InlineNotification severity="warning" title={successMessage ?? undefined}>
            {_t('Please update your email at the top of this page.')}
          </InlineNotification>
        )}
      </div>
      <div role="alert" aria-live="assertive" aria-atomic="true" css={styles.liveRegion}>
        {errorMessage && <InlineNotification severity="error">{errorMessage}</InlineNotification>}
      </div>
    </div>
  );
};

export default AccountLinkApple;
